/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import { Box, Flex, Text } from "@powerledger/ui-component-lib";
import { Field } from "formik";
import { FC } from "react";
import { useTranslation } from "react-i18next";

import { PRIVACY_POLICY_URL, TERMS_OF_ACCESS_URL, TRACEX_RULES_URL } from "@/app/lib/env-helpers";

import { Form } from "../form";
import { LegalCheckBoxesProps } from "./legal-check-boxes.types";
import { LegalCheckBox } from "./LegalCheckBox";

export const LEGAL_MESSAGE =
  "By proceeding, you agree, on behalf of the Applicant entity, for the Applicant to apply to become a Participant of the TraceX Marketplace provided by Powerledger and the Applicant to be bound by the rules and terms and conditions listed below.";

export const LegalCheckBoxes: FC<LegalCheckBoxesProps> = ({
  handleAcknowledgementChange,
  isCheckBoxDisabled,
  showLegalMessage,
}) => {
  const { t } = useTranslation();
  const allAccepted =
    isCheckBoxDisabled("rulesAccepted") &&
    isCheckBoxDisabled("privacyPolicyAccepted") &&
    isCheckBoxDisabled("privacyPolicyAccepted");
  return (
    <>
      {showLegalMessage && (
        <Box sx={{ color: "text", opacity: allAccepted ? 0.5 : 1, fontSize: 0, mt: 4, mb: 3 }}>
          <Text>{t(LEGAL_MESSAGE)}</Text>
        </Box>
      )}
      <Box sx={{ color: "text", opacity: allAccepted ? 0.5 : 1, fontSize: 0, mt: 4, mb: 3 }}>
        <Text>{t("Tick the boxes below to indicate your acceptance.")}</Text>
      </Box>
      <Flex sx={{ flexWrap: "wrap" }}>
        <Form.Item sx={{ mr: 4 }}>
          <Field
            name="rulesAccepted"
            component={() => (
              <LegalCheckBox
                id="rulesAccepted"
                name="rulesAccepted"
                title="TraceX Rules"
                disabled={isCheckBoxDisabled("rulesAccepted")}
                handleAcknowledgementChange={handleAcknowledgementChange}
                translatedFileKey="traceXRules"
                downloadUrl={TRACEX_RULES_URL}
              />
            )}
          />
        </Form.Item>
        <Form.Item sx={{ mr: 4 }}>
          <Field
            name="termsAccepted"
            component={() => (
              <LegalCheckBox
                id="termsAccepted"
                name="termsAccepted"
                title="Terms of Access"
                handleAcknowledgementChange={handleAcknowledgementChange}
                disabled={isCheckBoxDisabled("termsAccepted")}
                translatedFileKey="termsOfAccess"
                downloadUrl={TERMS_OF_ACCESS_URL}
              />
            )}
          />
        </Form.Item>
        <Form.Item>
          <Field
            name="privacyPolicyAccepted"
            component={() => (
              <LegalCheckBox
                id="privacyPolicyAccepted"
                name="privacyPolicyAccepted"
                title="Privacy Policy"
                handleAcknowledgementChange={handleAcknowledgementChange}
                disabled={isCheckBoxDisabled("privacyPolicyAccepted")}
                translatedFileKey="privacyPolicy"
                downloadUrl={PRIVACY_POLICY_URL}
              />
            )}
          />
        </Form.Item>
      </Flex>
    </>
  );
};
